import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../../../utils';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ContentDescription } from '../../components/shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { FileText, Video, File as FileIcon, Upload, X, Send, Eye } from 'lucide-react';
import { Button } from '../../../core/button';
import { Progress } from '../../../core/progress';
import { useMediaUpload } from '../../../hooks/use-media-upload';
import { FilePreviewDialog } from '../../components/shared/file-preview-dialog';

export const SUBMISSION_ACCEPTED_TYPES = [
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
];

export const SUBMISSION_MAX_SIZE = 500 * 1024 * 1024; // 500MB

interface UploadedFile {
  url: string;
  filename: string;
  contentType: string;
  fileSize: number;
}

interface FileUploadExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

function getFileTypeIcon(contentType: string) {
  if (contentType === 'application/pdf') {
    return <FileText className="h-5 w-5 text-red-500" />;
  }
  if (contentType?.startsWith('video/')) {
    return <Video className="h-5 w-5 text-blue-500" />;
  }
  if (contentType?.includes('presentation') || contentType?.includes('powerpoint')) {
    return <FileText className="h-5 w-5 text-orange-500" />;
  }
  return <FileIcon className="h-5 w-5 text-gray-400" />;
}

function formatFileSize(bytes: number | undefined | null): string {
  if (!bytes || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: FileUploadExerciseProps) {
  const initialFiles = useMemo<UploadedFile[]>(() => {
    const submission = content.lastSubmission;
    if (
      submission &&
      typeof submission === 'object' &&
      'type' in submission &&
      submission.type === ExerciseType.FileUpload &&
      'files' in submission &&
      Array.isArray((submission as any).files)
    ) {
      return (submission as any).files as UploadedFile[];
    }
    return [];
  }, [content.lastSubmission]);

  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(initialFiles);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewFile, setPreviewFile] = useState<UploadedFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadState, uploadMedia, resetUpload } = useMediaUpload();

  // Reset files when content changes (navigating between exercises)
  useEffect(() => {
    setUploadedFiles(initialFiles);
  }, [content.id, initialFiles]);

  const handleUploadFile = useCallback(async (file: File) => {
    // Validate file type
    if (!SUBMISSION_ACCEPTED_TYPES.includes(file.type)) {
      console.error(`File type not supported: ${file.type}`);
      return;
    }
    // Validate file size
    if (file.size > SUBMISSION_MAX_SIZE) {
      console.error(`File too large: ${file.size} bytes (max ${SUBMISSION_MAX_SIZE})`);
      return;
    }

    try {
      const result = await uploadMedia(file);
      if (result && result.mediaUrl) {
        setUploadedFiles((prev) => [
          ...prev,
          {
            url: result.mediaUrl,
            filename: file.name,
            contentType: file.type,
            fileSize: file.size,
          },
        ]);
        resetUpload();
      }
    } catch (err) {
      console.error('File upload failed:', err);
    }
  }, [uploadMedia, resetUpload]);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      for (let i = 0; i < files.length; i++) {
        handleUploadFile(files[i]);
      }
      // Reset input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [handleUploadFile]
  );

  const handleDrag = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const files = e.dataTransfer.files;
      if (!files) return;
      for (let i = 0; i < files.length; i++) {
        handleUploadFile(files[i]);
      }
    },
    [handleUploadFile]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type: ExerciseType.FileUpload as any,
        files: uploadedFiles,
      } as any);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isUploading =
    uploadState.status === 'requesting-urls' ||
    uploadState.status === 'uploading' ||
    uploadState.status === 'composing';

  const acceptString = SUBMISSION_ACCEPTED_TYPES.join(',');

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'space-y-6',
        variant === 'card' &&
          'rounded-[24px] border border-rose-100 bg-white/90 p-6 shadow-sm backdrop-blur'
      )}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {content.title && (
            <h3 className="text-xl font-semibold text-gray-900">
              {renderInlineMarkdown(content.title, 'file-upload-exercise-title')}
            </h3>
          )}
        </div>
      </div>

      <ContentDescription text={content.description} markdown />

      {/* Info badge */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-rose-50 to-pink-50 px-4 py-2 border border-rose-100">
          <Upload className="h-4 w-4 text-[#cf2e2e]" />
          <span className="text-sm text-gray-700 font-medium">File Upload</span>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1.5 border border-gray-200">
          <span className="text-xs text-gray-600">PDF, Video, PPT (max 500 MB)</span>
        </div>
      </div>

      {isReadOnly ? (
        /* Read-only mode: show file list with download links */
        <div className="rounded-[20px] border border-rose-100 bg-gradient-to-b from-white to-rose-50/30 p-5">
          {uploadedFiles.length > 0 ? (
            <div className="space-y-2">
              {uploadedFiles.map((file, idx) => (
                <div
                  key={file.url ?? idx}
                  className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-gray-100 shrink-0">
                    {getFileTypeIcon(file.contentType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.filename ?? 'Unknown file'}
                    </p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.fileSize)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreviewFile(file)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 transition-colors"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View
                  </button>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 rounded-md bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Download
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-100 mb-3">
                <Upload className="h-8 w-8 text-rose-300" />
              </div>
              <p className="text-sm text-gray-500">No files submitted yet.</p>
            </div>
          )}
        </div>
      ) : (
        /* Edit mode: upload zone + file list */
        <div className="space-y-4">
          {/* Upload zone */}
          <div
            className={cn(
              'rounded-[20px] border-2 border-dashed p-6 text-center transition-colors cursor-pointer',
              dragActive
                ? 'border-rose-400 bg-rose-50/50'
                : 'border-gray-300 hover:border-rose-300 bg-white/50'
            )}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileInputChange}
              accept={acceptString}
              className="hidden"
            />
            <Upload className="mx-auto h-10 w-10 text-gray-400 mb-3" />
            <p className="text-sm font-medium text-gray-700">
              Drag and drop files here, or click to browse
            </p>
            <p className="text-xs text-gray-500 mt-1">
              PDF, Video (MP4, WebM, MOV), PowerPoint (max 500 MB each)
            </p>
          </div>

          {/* Upload progress */}
          {isUploading && (
            <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <Progress value={uploadState.progress} className="w-full" />
              <p className="text-sm text-center text-gray-500">
                {uploadState.status === 'requesting-urls' && 'Preparing upload...'}
                {uploadState.status === 'uploading' &&
                  `Uploading... ${uploadState.progress ?? 0}%`}
                {uploadState.status === 'composing' && 'Finalizing upload...'}
              </p>
            </div>
          )}

          {/* Upload error */}
          {uploadState.status === 'error' && uploadState.error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {uploadState.error}
            </div>
          )}

          {/* Uploaded files list */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-2">
              {uploadedFiles.map((file, idx) => (
                <div
                  key={file.url ?? idx}
                  className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white border border-gray-100 shrink-0">
                    {getFileTypeIcon(file.contentType)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.filename}
                    </p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.fileSize)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPreviewFile(file)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-700 transition-colors"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(idx)}
                    className="shrink-0 rounded-md p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                    aria-label={`Remove ${file.filename}`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ExerciseFeedbackList feedback={feedback ?? []} pending={isSubmitting} />

      {onExerciseSubmit && !isReadOnly && (
        <div className="flex justify-center pt-2">
          <Button
            type="submit"
            disabled={uploadedFiles.length === 0 || isSubmitting || isUploading}
            variant="submit"
            size="submit"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            className="w-full sm:w-auto"
          >
            <Send className="h-4 w-4 mr-2" />
            Submit Files
          </Button>
        </div>
      )}
      <FilePreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} />
    </form>
  );
}
